import { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { ReactCompareSlider, ReactCompareSliderHandle, ReactCompareSliderImage } from 'react-compare-slider';

import styles from '@styles/passthru.module.scss';
import classNames from 'classnames';
import { Partner, carsPartners, cruisePartners, flightsPartners, hotelsPartners } from './animation-data';

interface PartnersAnimationProps {
	partner: string;
	vert: string;
	redirectToPartner: () => void;
}

const PartnersAnimation: FC<PartnersAnimationProps> = ({ partner, vert, redirectToPartner }) => {
	const [currentPosition, setCurrentPosition] = useState(0.1);
	const [partnerName, setPartnerName] = useState('');
	const [partnersToAnimate, setPartnersToAnimate] = useState<Partner[]>([]);
	const animationWrapperRef = useRef<HTMLDivElement>(null);

	const sliderImageStyles: CSSProperties = {
		maxWidth: '100%',
		width: '100%',
		height: 'auto',
		backgroundColor: '#fff',
		objectFit: 'contain',
		padding: '10px 0',
	};

	const RenderSlider = () => {
		const currentImage = useRef(0);
		const nextImage = useRef(1);
		const animationDuration = 850;

		useEffect(() => {
			setPartnerName(partner);
			setPartnersToAnimate(getPartnersToAnimate(partner));

			setTimeout(() => {
				setCurrentPosition(99);
			}, animationDuration);
		}, [partner]);

		useEffect(() => {
			if (animationWrapperRef.current) {
				animationWrapperRef.current?.classList.add(styles.animationWrapperFadeIn);
			}
		}, [partnersToAnimate]);

		const getVertPartners = (vert: string) => {
			switch (vert) {
				case 'cars':
					return carsPartners;
				case 'flights':
					return flightsPartners;
				case 'hotels':
					return hotelsPartners;
				case 'cruises':
					return cruisePartners;
				default:
					return [];
			}
		};

		const getPartnersToAnimate = (partnerName: string) => {
			const partners = getVertPartners(vert);

			if (!partnerName.length) {
				setTimeout(() => {
					redirectToPartner();
				}, 2000);
				return partners;
			}

			const partnerIndex = partners.findIndex((partner) => partner.name === partnerName);

			if (partnerIndex === -1) {
				setTimeout(() => {
					redirectToPartner();
				}, 2000);
				return partners;
			}

			const lastFourPartners = [...partners.slice(Math.max(0, partnerIndex - 3), partnerIndex + 1)];

			if (lastFourPartners.length < 4) {
				const remainingPartners = partners.slice(partnerIndex + 1);
				const missingPartners = 4 - lastFourPartners.length;
				const additionalPartners = remainingPartners.slice(0, missingPartners);
				const finalPartners = [...lastFourPartners, ...additionalPartners];

				// extract the selected partner and push it to the end of the array
				const partnerToMove = finalPartners.splice(
					finalPartners.findIndex((partner) => partner.name === partnerName),
					1
				)[0];
				finalPartners.push(partnerToMove);

				return finalPartners;
			}

			const partnerToMove = lastFourPartners.splice(
				lastFourPartners.findIndex((partner) => partner.name === partnerName),
				1
			)[0];
			lastFourPartners.push(partnerToMove);

			return lastFourPartners;
		};

		const isPartner = partnersToAnimate.findIndex((partner) => partner.name === partnerName);

		useEffect(() => {
			const partners = getVertPartners(vert);

			if (currentPosition === 99) {
				setTimeout(() => {
					if (currentImage.current === isPartner) {
						redirectToPartner();
						return;
					}

					nextImage.current = nextImage.current % 2 === 0 ? nextImage.current + 3 : nextImage.current + 1;
					nextImage.current >= partners.length ? (nextImage.current = 1) : nextImage.current;

					if (nextImage.current > partnersToAnimate.length) {
						nextImage.current = partnersToAnimate.findIndex((partner) => partner.name === partnerName);
						return;
					}

					setTimeout(() => {
						setCurrentPosition(0);
					}, 150);
				}, animationDuration);
			}

			if (currentPosition === 0) {
				setTimeout(() => {
					if (nextImage.current === isPartner) return;

					currentImage.current = currentImage.current % 2 === 0 ? currentImage.current + 3 : currentImage.current + 1;
					currentImage.current >= partners.length ? (currentImage.current = 0) : currentImage.current;

					setTimeout(() => {
						setCurrentPosition(99);
					}, 150);
				}, animationDuration);
			}
		}, [currentPosition, partnerName, partnersToAnimate]);

		return (
			<div className={styles.partnerLogos}>
				<div className={styles.animationWrapper}>
					{!!partnersToAnimate.length && (
						<>
							<div className={classNames(styles.top, styles.topLeft, styles.curvedLine)}></div>
							<div className={classNames(styles.top, styles.topRight, styles.curvedLine)}></div>
							<div className={classNames(styles.bottom, styles.bottomLeft, styles.curvedLine)}></div>
							<div className={classNames(styles.bottom, styles.bottomRight, styles.curvedLine)}></div>
							<div className={classNames(styles.animationWrapperFade)} ref={animationWrapperRef}>
								<ReactCompareSlider
									className={styles.logosSlider}
									position={currentPosition}
									handle={
										<ReactCompareSliderHandle
											buttonStyle={{ display: 'none' }}
											linesStyle={{ height: '100%', width: 5, pointerEvents: 'none', color: '#049dd9' }}
										/>
									}
									itemOne={
										<ReactCompareSliderImage
											src={partnersToAnimate[currentImage.current].image}
											alt={partnersToAnimate[currentImage.current].name}
											style={sliderImageStyles}
										/>
									}
									itemTwo={
										<ReactCompareSliderImage
											src={partnersToAnimate[nextImage.current].image}
											alt={partnersToAnimate[nextImage.current].name}
											style={sliderImageStyles}
										/>
									}
								/>
							</div>
						</>
					)}
				</div>
			</div>
		);
	};

	return (
		<div className="passhtru-2" id={styles.progressLoading} style={{ position: 'relative' }}>
			{RenderSlider()}
		</div>
	);
};

export default PartnersAnimation;
